/**
 * Детальная страница пациента в кабинете врача.
 *
 * Отображает:
 * - профиль и текущий статус пациента
 * - график динамики ИПС
 * - график динамики симптомов
 * - результаты тестов
 * - историю отчётов
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, BarChart, Bar, ReferenceLine,
} from 'recharts';
import { doctorAPI, edssAPI } from '../../api/client';
import ChartExportWrapper from '../../components/ChartExportButton';
import { Patient, AnalyticsStatus, DailyReport, ChartData, RiskFlag, EDSSAssessment, Relapse } from '../../types';

/** Цвета для линий симптомов на графике */
const SYMPTOM_COLORS = [
  '#2563eb', '#dc2626', '#16a34a', '#f59e0b',
  '#8b5cf6', '#ec4899', '#06b6d4',
];

const RISK_LABELS: Record<RiskFlag, string> = {
  high_score: 'Высокий ИПС',
  worsening_trend: 'Ухудшение динамики',
  extreme_symptom: 'Экстремальный симптом',
  missed_medication: 'Пропуск лекарств',
};

const PatientDetailPage: React.FC = () => {
  const { patientId } = useParams<{ patientId: string }>();
  const navigate = useNavigate();

  const [patient, setPatient] = useState<Patient | null>(null);
  const [status, setStatus] = useState<AnalyticsStatus | null>(null);
  const [chartData, setChartData] = useState<ChartData | null>(null);
  const [recentReports, setRecentReports] = useState<DailyReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState<{ id: number; text: string; doctor_name: string; created_at: string }[]>([]);
  const [noteText, setNoteText] = useState('');
  const [alertSettings, setAlertSettings] = useState<Record<string, number> | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showCompare, setShowCompare] = useState(false);
  const [compareFrom1, setCompareFrom1] = useState('');
  const [compareTo1, setCompareTo1] = useState('');
  const [compareFrom2, setCompareFrom2] = useState('');
  const [compareTo2, setCompareTo2] = useState('');
  const [compareResult, setCompareResult] = useState<any>(null);
  const [compareLoading, setCompareLoading] = useState(false);
  const [edssLatest, setEdssLatest] = useState<EDSSAssessment | null>(null);
  const [edssHistory, setEdssHistory] = useState<EDSSAssessment[]>([]);
  const [showEdssForm, setShowEdssForm] = useState(false);
  const [edssScore, setEdssScore] = useState<number>(0);
  const [edssNotes, setEdssNotes] = useState('');
  const [edssFS, setEdssFS] = useState<Record<string, number>>({
    pyramidal: 0, cerebellar: 0, brainstem: 0, sensory: 0,
    bowel_bladder: 0, visual: 0, cerebral: 0, other: 0,
  });
  const [relapses, setRelapses] = useState<Relapse[]>([]);

  useEffect(() => {
    let cancelled = false;
    const loadData = async () => {
      if (!patientId) return;
      const id = Number(patientId);

      try {
        const [detailRes, chartRes] = await Promise.all([
          doctorAPI.getPatientDetail(id),
          doctorAPI.getChartData(id, 30),
        ]);

        if (cancelled) return;
        setPatient(detailRes.data.patient);
        setStatus(detailRes.data.status);
        setRecentReports(detailRes.data.recent_reports);
        setChartData(chartRes.data);

        const [notesRes, alertRes, edssRes, edssHistRes, relapsesRes] = await Promise.all([
          doctorAPI.getNotes(id),
          doctorAPI.getAlertSettings(id),
          edssAPI.getLatest(id),
          edssAPI.getHistory(id),
          doctorAPI.getPatientRelapses(id),
        ]);
        if (cancelled) return;
        setNotes(notesRes.data.notes);
        setAlertSettings(alertRes.data.settings);
        if (edssRes.data.assessment) setEdssLatest(edssRes.data.assessment);
        setEdssHistory(edssHistRes.data.assessments || []);
        setRelapses(relapsesRes.data.relapses || []);
      } catch (err) {
        if (!cancelled) console.error('Ошибка загрузки данных пациента:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadData();
    return () => { cancelled = true; };
  }, [patientId]);

  const handleExport = async (type: 'reports' | 'tests') => {
    if (!patientId) return;
    let url: string | null = null;
    try {
      const res = await doctorAPI.exportPatientData(Number(patientId), type);
      url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}_${patientId}.csv`;
      a.click();
    } catch (err) {
      console.error('Ошибка экспорта:', err);
    } finally {
      if (url) window.URL.revokeObjectURL(url);
    }
  };

  const handleDownloadPdf = async () => {
    if (!patientId) return;
    let url: string | null = null;
    try {
      const res = await doctorAPI.downloadPdfReport(Number(patientId));
      url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `report_${patientId}.pdf`;
      a.click();
    } catch (err) {
      console.error('Ошибка скачивания PDF:', err);
    } finally {
      if (url) window.URL.revokeObjectURL(url);
    }
  };

  const handleEdssSubmit = async () => {
    if (!patientId) return;
    try {
      const res = await edssAPI.createAssessment({
        patient_id: Number(patientId),
        score: edssScore,
        functional_systems: edssFS,
        notes: edssNotes || undefined,
      });
      const newAssessment = res.data.assessment;
      setEdssLatest(newAssessment);
      setEdssHistory([newAssessment, ...edssHistory]);
      setShowEdssForm(false);
      setEdssScore(0);
      setEdssNotes('');
      setEdssFS({
        pyramidal: 0, cerebellar: 0, brainstem: 0, sensory: 0,
        bowel_bladder: 0, visual: 0, cerebral: 0, other: 0,
      });
    } catch (err) {
      console.error('Ошибка создания EDSS:', err);
    }
  };

  if (loading) return <div style={{ padding: 40 }}>Загрузка...</div>;
  if (!patient || !status) return <div style={{ padding: 40 }}>Пациент не найден</div>;

  return (
    <div>
      <button style={styles.backBtn} onClick={() => navigate('/doctor/patients')}>
        &larr; К списку пациентов
      </button>

      {/* Шапка с информацией */}
      <div style={styles.header}>
        <div>
          <h1 style={{ margin: 0 }}>
            {patient.last_name} {patient.first_name}
          </h1>
          <div style={styles.meta}>
            {patient.birth_date && <span>Дата рождения: {patient.birth_date}</span>}
            {patient.diagnosis_date && <span>Диагноз: {patient.diagnosis_date}</span>}
            {patient.ms_type && <span>Тип РС: {patient.ms_type}</span>}
          </div>
        </div>

        <div style={styles.scoreBlock}>
          <div style={styles.scoreLabel}>ИПС</div>
          <div style={{
            ...styles.scoreValue,
            color: status.integrated_score >= 7 ? '#dc2626' :
                   status.integrated_score >= 4 ? '#f59e0b' : '#16a34a',
          }}>
            {status.integrated_score.toFixed(1)}
          </div>
          <div style={{
            ...styles.trendBadge,
            color: status.trend === 'critical' ? '#dc2626' :
                   status.trend === 'worsening' ? '#f59e0b' :
                   status.trend === 'improving' ? '#16a34a' : '#2563eb',
          }}>
            {status.trend_ru} ({status.trend_delta > 0 ? '+' : ''}{status.trend_delta.toFixed(1)})
          </div>
        </div>
      </div>

      {/* Экспорт и печать */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button style={styles.exportBtn} onClick={() => handleExport('reports')}>
          Экспорт дневника (CSV)
        </button>
        <button style={styles.exportBtn} onClick={() => handleExport('tests')}>
          Экспорт тестов (CSV)
        </button>
        <button
          style={styles.exportBtn}
          onClick={() => {
            const url = `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/doctor/patients/${patientId}/report-html`;
            const token = localStorage.getItem('auth_token');
            window.open(`${url}?_token=${token}`, '_blank');
          }}
        >
          Печать отчёта
        </button>
        <button style={{ ...styles.exportBtn, backgroundColor: '#2563eb', color: '#fff', border: 'none' }} onClick={handleDownloadPdf}>
          Скачать PDF
        </button>
      </div>

      {/* Настройки уведомлений */}
      <div style={{ marginBottom: 16 }}>
        <button
          style={{ ...styles.exportBtn, fontSize: 13 }}
          onClick={() => setShowSettings(!showSettings)}
        >
          {showSettings ? 'Скрыть настройки порогов' : 'Настройки порогов уведомлений'}
        </button>
        {showSettings && alertSettings && (
          <div style={{ ...styles.chartCard, marginTop: 8 }}>
            {[
              { key: 'high_score_threshold', label: 'Порог ИПС', step: 0.5, min: 1, max: 10 },
              { key: 'worsening_threshold', label: 'Порог ухудшения (дельта)', step: 0.5, min: 0.5, max: 5 },
              { key: 'extreme_symptom_value', label: 'Экстремальный симптом (>=)', step: 1, min: 5, max: 10 },
              { key: 'extreme_symptom_days', label: 'Дней подряд для экстремального', step: 1, min: 1, max: 7 },
              { key: 'missed_medication_days', label: 'Пропуск лекарств (дней)', step: 1, min: 1, max: 7 },
            ].map(({ key, label, step, min, max }) => (
              <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <label style={{ width: 260, fontSize: 14 }}>{label}:</label>
                <input
                  type="number" step={step} min={min} max={max}
                  value={alertSettings[key] ?? ''}
                  onChange={(e) => setAlertSettings({ ...alertSettings, [key]: Number(e.target.value) })}
                  style={{ width: 80, padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                />
              </div>
            ))}
            <button
              style={{ ...styles.exportBtn, backgroundColor: '#2563eb', color: '#fff', border: 'none', marginTop: 8 }}
              onClick={async () => {
                if (!patientId || !alertSettings) return;
                await doctorAPI.updateAlertSettings(Number(patientId), alertSettings);
                setShowSettings(false);
              }}
            >
              Сохранить пороги
            </button>
          </div>
        )}
      </div>

      {/* Флаги риска */}
      {status.risk_flags.length > 0 && (
        <div style={styles.riskBanner}>
          <strong>Факторы риска:</strong>{' '}
          {status.risk_flags.map((f) => RISK_LABELS[f]).join(', ')}
        </div>
      )}

      {/* Графики */}
      {chartData && (
        <div style={styles.chartsGrid}>
          {/* 1. Динамика ИПС */}
          <div style={styles.chartCard}>
            <ChartExportWrapper fileName={`ips_patient_${patientId}`}>
              <h3 style={styles.chartTitle}>Динамика ИПС</h3>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={chartData.integrated_score_history}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis domain={[0, 10]} />
                  <Tooltip />
                  <ReferenceLine y={7} stroke="#dc2626" strokeDasharray="5 5" label="Порог риска" />
                  <Line
                    type="monotone" dataKey="score" stroke="#2563eb"
                    strokeWidth={2} dot={{ r: 3 }} name="ИПС"
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartExportWrapper>
          </div>

          {/* 2. Динамика симптомов */}
          <div style={styles.chartCard}>
            <ChartExportWrapper fileName={`symptoms_patient_${patientId}`}>
              <h3 style={styles.chartTitle}>Динамика симптомов</h3>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={chartData.symptom_dynamics}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis domain={[0, 10]} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {chartData.symptom_names.map((name, i) => (
                    <Line
                      key={name}
                      type="monotone"
                      dataKey={name}
                      stroke={SYMPTOM_COLORS[i % SYMPTOM_COLORS.length]}
                      strokeWidth={1.5}
                      dot={false}
                      name={name}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </ChartExportWrapper>
          </div>

          {/* 3. Результаты тестов */}
          {chartData.test_results.length > 0 && (
            <div style={{ ...styles.chartCard, gridColumn: '1 / -1' }}>
              <ChartExportWrapper fileName={`tests_patient_${patientId}`}>
                <h3 style={styles.chartTitle}>Результаты тестов</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={chartData.test_results}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis domain={[0, 10]} />
                    <Tooltip />
                    <Bar dataKey="normalized_score" fill="#8b5cf6" name="Балл (норм.)" />
                  </BarChart>
                </ResponsiveContainer>
              </ChartExportWrapper>
            </div>
          )}
        </div>
      )}

      {/* Сравнение периодов */}
      <h2 style={{ marginTop: 32, marginBottom: 16 }}>
        Сравнение периодов
        <button
          style={{ ...styles.exportBtn, marginLeft: 12, fontSize: 13 }}
          onClick={() => setShowCompare(!showCompare)}
        >
          {showCompare ? 'Скрыть' : 'Показать'}
        </button>
      </h2>
      {showCompare && (
        <div style={styles.chartCard}>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 12, alignItems: 'flex-end' }}>
            <div>
              <label style={styles.compareLabel}>Период 1</label>
              <div style={{ display: 'flex', gap: 6 }}>
                <input type="date" value={compareFrom1} onChange={e => setCompareFrom1(e.target.value)} style={styles.dateInput} />
                <span style={{ lineHeight: '36px' }}>—</span>
                <input type="date" value={compareTo1} onChange={e => setCompareTo1(e.target.value)} style={styles.dateInput} />
              </div>
            </div>
            <div>
              <label style={styles.compareLabel}>Период 2</label>
              <div style={{ display: 'flex', gap: 6 }}>
                <input type="date" value={compareFrom2} onChange={e => setCompareFrom2(e.target.value)} style={styles.dateInput} />
                <span style={{ lineHeight: '36px' }}>—</span>
                <input type="date" value={compareTo2} onChange={e => setCompareTo2(e.target.value)} style={styles.dateInput} />
              </div>
            </div>
            <button
              style={{ ...styles.exportBtn, backgroundColor: '#2563eb', color: '#fff', border: 'none', height: 36 }}
              disabled={compareLoading || !compareFrom1 || !compareTo1 || !compareFrom2 || !compareTo2}
              onClick={async () => {
                if (!patientId) return;
                setCompareLoading(true);
                try {
                  const res = await doctorAPI.comparePeriods(Number(patientId), {
                    from1: compareFrom1, to1: compareTo1,
                    from2: compareFrom2, to2: compareTo2,
                  });
                  setCompareResult(res.data);
                } catch (err) {
                  console.error(err);
                } finally {
                  setCompareLoading(false);
                }
              }}
            >
              {compareLoading ? 'Загрузка...' : 'Сравнить'}
            </button>
          </div>

          {compareResult && (
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 12 }}>
              <thead>
                <tr>
                  <th style={styles.compTh}>Показатель</th>
                  <th style={styles.compTh}>
                    Период 1<br /><span style={{ fontWeight: 400, fontSize: 11 }}>{compareResult.period1.from} — {compareResult.period1.to}</span>
                  </th>
                  <th style={styles.compTh}>
                    Период 2<br /><span style={{ fontWeight: 400, fontSize: 11 }}>{compareResult.period2.from} — {compareResult.period2.to}</span>
                  </th>
                  <th style={styles.compTh}>Дельта</th>
                </tr>
              </thead>
              <tbody>
                {compareResult.period1.stats && compareResult.period2.stats ? (
                  <>
                    <tr>
                      <td style={styles.compTd}>Записей</td>
                      <td style={styles.compTd}>{compareResult.period1.stats.report_count}</td>
                      <td style={styles.compTd}>{compareResult.period2.stats.report_count}</td>
                      <td style={styles.compTd}>—</td>
                    </tr>
                    <tr>
                      <td style={styles.compTd}>Средн. самочувствие</td>
                      <td style={styles.compTd}>{compareResult.period1.stats.avg_feeling}</td>
                      <td style={styles.compTd}>{compareResult.period2.stats.avg_feeling}</td>
                      {(() => {
                        const d = +(compareResult.period2.stats.avg_feeling - compareResult.period1.stats.avg_feeling).toFixed(1);
                        return <td style={{ ...styles.compTd, color: d > 0 ? '#dc2626' : d < 0 ? '#16a34a' : '#374151', fontWeight: 600 }}>
                          {d > 0 ? '+' : ''}{d}
                        </td>;
                      })()}
                    </tr>
                    {compareResult.period1.stats.avg_test_score !== null && compareResult.period2.stats.avg_test_score !== null && (
                      <tr>
                        <td style={styles.compTd}>Средн. тест (норм.)</td>
                        <td style={styles.compTd}>{compareResult.period1.stats.avg_test_score}</td>
                        <td style={styles.compTd}>{compareResult.period2.stats.avg_test_score}</td>
                        {(() => {
                          const d = +(compareResult.period2.stats.avg_test_score - compareResult.period1.stats.avg_test_score).toFixed(1);
                          return <td style={{ ...styles.compTd, color: d > 0 ? '#dc2626' : d < 0 ? '#16a34a' : '#374151', fontWeight: 600 }}>
                            {d > 0 ? '+' : ''}{d}
                          </td>;
                        })()}
                      </tr>
                    )}
                    {/* Симптомы */}
                    {Object.keys(compareResult.period1.stats.symptom_averages).map((name) => {
                      const v1 = compareResult.period1.stats.symptom_averages[name] ?? 0;
                      const v2 = compareResult.period2.stats.symptom_averages[name] ?? 0;
                      const d = +(v2 - v1).toFixed(1);
                      return (
                        <tr key={name}>
                          <td style={styles.compTd}>{name}</td>
                          <td style={styles.compTd}>{v1}</td>
                          <td style={styles.compTd}>{v2}</td>
                          <td style={{ ...styles.compTd, color: d > 0 ? '#dc2626' : d < 0 ? '#16a34a' : '#374151', fontWeight: 600 }}>
                            {d > 0 ? '+' : ''}{d}
                          </td>
                        </tr>
                      );
                    })}
                  </>
                ) : (
                  <tr>
                    <td colSpan={4} style={{ ...styles.compTd, textAlign: 'center', color: '#6b7280' }}>
                      Нет данных за один или оба периода
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* EDSS */}
      <h2 style={{ marginTop: 32, marginBottom: 16 }}>
        Шкала EDSS
        {edssLatest && (
          <span style={{ marginLeft: 12, fontSize: 14, fontWeight: 400, color: 'var(--text-secondary)' }}>
            Текущий балл: <strong style={{ color: edssLatest.score >= 6 ? '#dc2626' : edssLatest.score >= 3 ? '#f59e0b' : '#16a34a' }}>
              {edssLatest.score}
            </strong> (от {new Date(edssLatest.assessed_at).toLocaleDateString('ru-RU')})
          </span>
        )}
      </h2>
      <div style={styles.chartCard}>
        <button
          style={{ ...styles.exportBtn, marginBottom: 12 }}
          onClick={() => setShowEdssForm(!showEdssForm)}
        >
          {showEdssForm ? 'Скрыть форму' : 'Новая оценка EDSS'}
        </button>

        {showEdssForm && (
          <div style={{ marginBottom: 16, padding: 16, border: '1px solid var(--border-color)', borderRadius: 8 }}>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Общий балл EDSS (0-10, шаг 0.5)</label>
              <input
                type="number" step={0.5} min={0} max={10} value={edssScore}
                onChange={e => setEdssScore(Number(e.target.value))}
                style={{ width: 100, padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8, marginBottom: 12 }}>
              {Object.entries({
                pyramidal: 'Пирамидная', cerebellar: 'Мозжечковая', brainstem: 'Стволовая',
                sensory: 'Чувствительная', bowel_bladder: 'Тазовые функции', visual: 'Зрительная',
                cerebral: 'Церебральная', other: 'Другое',
              }).map(([key, label]) => (
                <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <label style={{ fontSize: 12, width: 130 }}>{label}:</label>
                  <input
                    type="number" min={0} max={6} value={edssFS[key] ?? 0}
                    onChange={e => setEdssFS({ ...edssFS, [key]: Number(e.target.value) })}
                    style={{ width: 50, padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13 }}
                  />
                </div>
              ))}
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Комментарий</label>
              <textarea
                value={edssNotes} onChange={e => setEdssNotes(e.target.value)}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, minHeight: 50, resize: 'vertical' }}
              />
            </div>
            <button
              style={{ ...styles.exportBtn, backgroundColor: '#2563eb', color: '#fff', border: 'none' }}
              onClick={handleEdssSubmit}
            >
              Сохранить оценку
            </button>
          </div>
        )}

        {edssHistory.length > 0 ? (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={styles.compTh}>Дата</th>
                <th style={styles.compTh}>Балл</th>
                <th style={styles.compTh}>Врач</th>
                <th style={styles.compTh}>Комментарий</th>
              </tr>
            </thead>
            <tbody>
              {edssHistory.map(a => (
                <tr key={a.id}>
                  <td style={styles.compTd}>{new Date(a.assessed_at).toLocaleDateString('ru-RU')}</td>
                  <td style={{ ...styles.compTd, fontWeight: 600, color: a.score >= 6 ? '#dc2626' : a.score >= 3 ? '#f59e0b' : '#16a34a' }}>{a.score}</td>
                  <td style={styles.compTd}>{a.doctor_name || '—'}</td>
                  <td style={styles.compTd}>{a.notes || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p style={{ color: '#6b7280', fontSize: 14 }}>Оценок EDSS пока нет</p>
        )}
      </div>

      {/* Обострения */}
      <h2 style={{ marginTop: 32, marginBottom: 16 }}>
        Обострения
        <span style={{ marginLeft: 12, fontSize: 14, fontWeight: 400, color: 'var(--text-secondary)' }}>
          ({relapses.length})
        </span>
      </h2>
      <div style={styles.chartCard}>
        {relapses.length === 0 ? (
          <p style={{ color: '#6b7280', fontSize: 14 }}>Обострений не зафиксировано</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={styles.compTh}>Начало</th>
                <th style={styles.compTh}>Окончание</th>
                <th style={styles.compTh}>Тяжесть</th>
                <th style={styles.compTh}>Лечение</th>
                <th style={styles.compTh}>Заметки</th>
              </tr>
            </thead>
            <tbody>
              {relapses.map(r => (
                <tr key={r.id}>
                  <td style={styles.compTd}>{r.start_date}</td>
                  <td style={styles.compTd}>
                    {r.end_date || <span style={{ color: '#dc2626', fontWeight: 600, fontSize: 12 }}>Активное</span>}
                  </td>
                  <td style={{
                    ...styles.compTd,
                    color: r.severity === 'severe' ? '#dc2626' : r.severity === 'moderate' ? '#f59e0b' : '#16a34a',
                    fontWeight: 600,
                  }}>
                    {r.severity === 'severe' ? 'Тяжёлое' : r.severity === 'moderate' ? 'Умеренное' : 'Лёгкое'}
                  </td>
                  <td style={styles.compTd}>{r.treatment || '—'}</td>
                  <td style={styles.compTd}>{r.notes || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Заметки врача */}
      <h2 style={{ marginTop: 32, marginBottom: 16 }}>Заметки врача</h2>
      <div style={styles.chartCard}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <textarea
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Наблюдения, рекомендации..."
            style={{ flex: 1, padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, resize: 'vertical', minHeight: 60 }}
            rows={2}
          />
          <button
            style={{ ...styles.exportBtn, alignSelf: 'flex-end', backgroundColor: '#2563eb', color: '#fff', border: 'none' }}
            onClick={async () => {
              if (!noteText.trim() || !patientId) return;
              const res = await doctorAPI.createNote(Number(patientId), noteText);
              setNotes([res.data.note, ...notes]);
              setNoteText('');
            }}
          >
            Добавить
          </button>
        </div>
        {notes.length === 0 ? (
          <p style={{ color: '#6b7280', fontSize: 14 }}>Заметок пока нет</p>
        ) : (
          notes.map((n) => (
            <div key={n.id} style={{ padding: '10px 0', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <p style={{ margin: 0, fontSize: 14 }}>{n.text}</p>
                <span style={{ fontSize: 12, color: '#9ca3af' }}>
                  {new Date(n.created_at).toLocaleString('ru-RU')}
                </span>
              </div>
              <button
                style={{ background: '#fef2f2', color: '#dc2626', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 12 }}
                onClick={async () => {
                  await doctorAPI.deleteNote(Number(patientId), n.id);
                  setNotes(notes.filter((x) => x.id !== n.id));
                }}
              >
                X
              </button>
            </div>
          ))
        )}
      </div>

      {/* Последние отчёты */}
      <h2 style={{ marginTop: 32, marginBottom: 16 }}>Последние записи дневника</h2>
      {recentReports.length === 0 ? (
        <p style={{ color: '#6b7280' }}>Записей нет</p>
      ) : (
        recentReports.map((report) => (
          <div key={report.id} style={styles.reportCard}>
            <div style={styles.reportHeader}>
              <strong>{report.date}</strong>
              <span>Самочувствие: {report.overall_feeling}/10</span>
            </div>
            <div style={styles.symptoms}>
              {report.symptoms.map((s) => (
                <span key={s.symptom_id} style={{
                  ...styles.badge,
                  backgroundColor: s.severity >= 7 ? '#fef2f2' :
                                   s.severity >= 4 ? '#fffbeb' : '#f0fdf4',
                  color: s.severity >= 7 ? '#dc2626' :
                         s.severity >= 4 ? '#b45309' : '#166534',
                }}>
                  {s.name_ru}: {s.severity}
                </span>
              ))}
            </div>
            {report.notes && (
              <p style={{ fontSize: 13, color: '#6b7280', marginTop: 8 }}>{report.notes}</p>
            )}
          </div>
        ))
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  backBtn: {
    background: 'none', border: 'none', color: '#2563eb',
    cursor: 'pointer', fontSize: 14, padding: 0, marginBottom: 16,
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    backgroundColor: 'var(--bg-card)', padding: 24, borderRadius: 12,
    boxShadow: 'var(--shadow-card)', marginBottom: 16,
  },
  meta: {
    display: 'flex', gap: 16, fontSize: 13, color: 'var(--text-secondary)', marginTop: 8,
    flexWrap: 'wrap',
  },
  scoreBlock: { textAlign: 'center', minWidth: 120 },
  scoreLabel: { fontSize: 12, color: 'var(--text-secondary)' },
  scoreValue: { fontSize: 40, fontWeight: 700, lineHeight: 1.2 },
  trendBadge: { fontSize: 13, fontWeight: 600 },
  riskBanner: {
    backgroundColor: 'var(--bg-danger)', border: '1px solid #fecaca',
    color: '#dc2626', padding: 14, borderRadius: 12,
    marginBottom: 16, fontSize: 14,
  },
  chartsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))',
    gap: 16,
  },
  chartCard: {
    backgroundColor: 'var(--bg-card)', padding: 20, borderRadius: 12,
    boxShadow: 'var(--shadow-card)',
  },
  chartTitle: { fontSize: 16, marginBottom: 16, color: 'var(--text-primary)' },
  reportCard: {
    backgroundColor: 'var(--bg-card)', padding: 16, borderRadius: 12,
    boxShadow: 'var(--shadow-card)', marginBottom: 10,
  },
  reportHeader: {
    display: 'flex', justifyContent: 'space-between', marginBottom: 8,
    fontSize: 14,
  },
  symptoms: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  badge: {
    padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 500,
  },
  exportBtn: {
    background: 'var(--bg-card)', border: '1px solid var(--border-color)', padding: '8px 16px',
    borderRadius: 8, cursor: 'pointer', fontSize: 13, color: 'var(--text-primary)',
  },
  compareLabel: { fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 },
  dateInput: { padding: '6px 10px', border: '1px solid var(--border-color)', borderRadius: 6, fontSize: 13 },
  compTh: {
    textAlign: 'left' as const, padding: '8px 12px', backgroundColor: 'var(--bg-table-header)',
    fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', borderBottom: '1px solid var(--border-color)',
  },
  compTd: { padding: '8px 12px', fontSize: 13, borderBottom: '1px solid var(--border-light)' },
};

export default PatientDetailPage;
